import React, { useState, useEffect } from "react";
import "./App.css";
import { HashRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Checkbox } from "./components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { Plus, Users, Building2, Target, Phone, Mail, MapPin, Calendar, CheckCircle, XCircle, Clock, Filter, Search, Copy, ExternalLink, Menu, X, Edit, Lock, Eye, LogOut, User, Trash2, Settings, BarChart3, Send } from "lucide-react";

// Import components
import AgenziaOnboarding from "./AgenziaOnboarding";
import CandidatoOnboarding from "./CandidatoOnboarding";
import OnboardingSuccess from "./OnboardingSuccess";
import DashboardCliente from "./DashboardCliente";
import DashboardAgenzia from "./DashboardAgenzia";
import AdminPanel from "./AdminPanel";
import Feed from "./Feed";
import AgenzieList from "./components/AgenzieList.js";
import CandidatoDashboard from "./CandidatoDashboard";
import LandingAccesso from "./components/LandingAccesso";
import SceltaTipoUtente from "./components/SceltaTipoUtente";
import LoginPage from "./components/LoginPage";
import MobileBottomNav from "./components/MobileBottomNav";
import { agentAdvancerSenzaSfondo } from './images';
import DemoMain from "./demo/DemoMain";

// Wrapper component to extract candidatoId from URL params
const CandidatoDashboardWrapper = () => {
  const { candidatoId } = useParams();
  return <CandidatoDashboard candidatoId={candidatoId} />;
};

// Wrapper component to extract agenziaId from URL params
const AgenziaDashboardWrapper = () => {
  const { agenziaId } = useParams();
  return <DashboardAgenzia agenziaId={agenziaId} />;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = process.env.REACT_APP_API_BASE || `${BACKEND_URL}/api`;

// Enums matching backend
const StatoPipeline = {
  NUOVO: "Nuovo",
  CONTATTATO: "Contattato", 
  MODULO_INVIATO: "Modulo inviato",
  FORM_COMPILATO: "Form compilato",
  IN_ATTESA_COLLOQUIO: "In attesa colloquio",
  COLLOQUIO_FISSATO: "Colloquio fissato",
  INSERITO: "Inserito",
  SCARTATO: "Scartato"
};

const Provenienza = {
  TELEFONISTA: "Telefonista",
  FACEBOOK_ADS: "Facebook Ads",
  INSTAGRAM_ADS: "Instagram Ads",
  ALTRO: "Altro"
};

const ProfiloRicercato = {
  CON_ESPERIENZA: "Con esperienza",
  SENZA_ESPERIENZA: "Senza esperienza", 
  INDIFFERENTE: "Indifferente"
};

// Utility functions - Restored Previous Colors
const getStatusColor = (status) => {
  const colors = {
    [StatoPipeline.NUOVO]: "bg-blue-100 text-blue-800 border-blue-300 shadow-sm",
    [StatoPipeline.CONTATTATO]: "bg-yellow-100 text-yellow-800 border-yellow-300 shadow-sm",
    [StatoPipeline.MODULO_INVIATO]: "bg-orange-100 text-orange-800 border-orange-300 shadow-sm",
    [StatoPipeline.FORM_COMPILATO]: "bg-purple-100 text-purple-800 border-purple-300 shadow-sm",
    [StatoPipeline.IN_ATTESA_COLLOQUIO]: "bg-pink-100 text-pink-800 border-pink-300 shadow-sm",
    [StatoPipeline.COLLOQUIO_FISSATO]: "bg-cyan-100 text-cyan-800 border-cyan-300 shadow-sm",
    [StatoPipeline.INSERITO]: "bg-green-100 text-green-800 border-green-300 shadow-sm",
    [StatoPipeline.SCARTATO]: "bg-red-100 text-red-800 border-red-300 shadow-sm"
  };
  return colors[status] || "bg-gray-100 text-gray-800 border-gray-300 shadow-sm";
};

const getScoreColor = (score) => {
  if (score >= 80) return "text-blue-600 font-bold";
  if (score >= 60) return "text-blue-500 font-semibold";
  if (score >= 40) return "text-blue-400 font-medium";
  return "text-gray-500";
};

// Nuova Agenzia Modal Component
const NuovaAgenziaModal = ({ isOpen, onClose, onAgenziaAdded }) => {
  const [formData, setFormData] = useState({
    nome_agenzia: "",
    zona: "",
    contatto_referente: "",
    profilo_ricercato: "Indifferente",
    posti_disponibili: 10,
    stato: "Attiva",
    note_interne: "",
    email: "",
    telefono: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome_agenzia.trim() || !formData.zona.trim()) {
      toast.error("Nome agenzia e zona sono obbligatori");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/v1/agenzie`, formData);
      toast.success("Agenzia aggiunta con successo");
      onAgenziaAdded();
      onClose();
      setFormData({
        nome_agenzia: "",
        zona: "",
        contatto_referente: "",
        profilo_ricercato: "Indifferente",
        posti_disponibili: 10,
        stato: "Attiva",
        note_interne: "",
        email: "",
        telefono: ""
      });
    } catch (error) {
      console.error("Errore nell'aggiunta agenzia:", error);
      toast.error("Errore nell'aggiunta agenzia");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl max-w-2xl w-full p-8 shadow-2xl border border-white/20 animate-in slide-in-from-bottom-4 duration-300 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Plus className="w-5 h-5 text-white" />
            </div>
            Nuova Agenzia
          </h3>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="rounded-full w-8 h-8 p-0 hover:bg-gray-100 transition-colors duration-200"
          >
            <X className="w-4 h-4 text-gray-500" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label htmlFor="nome_agenzia" className="text-sm font-medium text-gray-700">Nome Agenzia *</Label>
              <Input
                id="nome_agenzia"
                value={formData.nome_agenzia}
                onChange={(e) => setFormData({...formData, nome_agenzia: e.target.value})}
                placeholder="Nome dell'agenzia"
                required
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="zona" className="text-sm font-medium text-gray-700">Zona *</Label>
              <Input
                id="zona"
                value={formData.zona}
                onChange={(e) => setFormData({...formData, zona: e.target.value})}
                placeholder="Zona di riferimento"
                required
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="contatto_referente" className="text-sm font-medium text-gray-700">Referente</Label>
              <Input
                id="contatto_referente"
                value={formData.contatto_referente}
                onChange={(e) => setFormData({...formData, contatto_referente: e.target.value})}
                placeholder="Nome del referente"
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@agenzia.it"
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="telefono" className="text-sm font-medium text-gray-700">Telefono</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                placeholder="+39 000 000 0000"
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="profilo_ricercato" className="text-sm font-medium text-gray-700">Profilo Ricercato</Label>
              <Select value={formData.profilo_ricercato} onValueChange={(value) => setFormData({...formData, profilo_ricercato: value})}>
                <SelectTrigger className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 shadow-xl rounded-2xl">
                  <SelectItem value="Con esperienza" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Con esperienza</SelectItem>
                  <SelectItem value="Senza esperienza" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Senza esperienza</SelectItem>
                  <SelectItem value="Indifferente" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Indifferente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label htmlFor="posti_disponibili" className="text-sm font-medium text-gray-700">Posti Disponibili</Label>
              <Input
                id="posti_disponibili"
                type="number"
                min="0"
                value={formData.posti_disponibili}
                onChange={(e) => setFormData({...formData, posti_disponibili: parseInt(e.target.value) || 0})}
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="stato" className="text-sm font-medium text-gray-700">Stato</Label>
              <Select value={formData.stato} onValueChange={(value) => setFormData({...formData, stato: value})}>
                <SelectTrigger className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 shadow-xl rounded-2xl">
                  <SelectItem value="Attiva" className="text-gray-900 hover:bg-green-50 rounded-xl m-1">Attiva</SelectItem>
                  <SelectItem value="Sospesa" className="text-gray-900 hover:bg-yellow-50 rounded-xl m-1">Sospesa</SelectItem>
                  <SelectItem value="Inattiva" className="text-gray-900 hover:bg-red-50 rounded-xl m-1">Inattiva</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <Label htmlFor="note_interne" className="text-sm font-medium text-gray-700">Note Interne</Label>
            <Textarea
              id="note_interne"
              value={formData.note_interne}
              onChange={(e) => setFormData({...formData, note_interne: e.target.value})}
              placeholder="Note interne sull'agenzia..."
              rows={3}
              className="bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="flex-1 h-12 rounded-2xl border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Annulla
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Salvando...
                </div>
              ) : (
                "Crea Agenzia"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Nuovo Candidato Modal Component
const NuovoCandidatoModal = ({ isOpen, onClose, onCandidatoAdded }) => {
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    telefono: "",
    email: "",
    zona_interesse: "",
    esperienza_flag: false,
    anni_esperienza: 0,
    provenienza: "Telefonista",
    stato_pipeline: "Nuovo",
    note_telefonista: "",
    consenso_gdpr: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim() || !formData.cognome.trim() || !formData.telefono.trim()) {
      toast.error("Nome, cognome e telefono sono obbligatori");
      return;
    }

    if (!formData.consenso_gdpr) {
      toast.error("Il consenso GDPR è obbligatorio");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/candidati`, {
        ...formData,
        candidate_score: Math.floor(Math.random() * 40) + 40 // Random score 40-80
      });
      toast.success("Candidato aggiunto con successo");
      onCandidatoAdded();
      onClose();
      setFormData({
        nome: "",
        cognome: "",
        telefono: "",
        email: "",
        zona_interesse: "",
        esperienza_flag: false,
        anni_esperienza: 0,
        provenienza: "Telefonista",
        stato_pipeline: "Nuovo",
        note_telefonista: "",
        consenso_gdpr: false
      });
    } catch (error) {
      console.error("Errore nell'aggiunta candidato:", error);
      toast.error("Errore nell'aggiunta candidato");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl max-w-2xl w-full p-8 shadow-2xl border border-white/20 animate-in slide-in-from-bottom-4 duration-300 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Users className="w-5 h-5 text-white" />
            </div>
            Nuovo Candidato
          </h3>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="rounded-full w-8 h-8 p-0 hover:bg-gray-100 transition-colors duration-200"
          >
            <X className="w-4 h-4 text-gray-500" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label htmlFor="nome" className="text-sm font-medium text-gray-700">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Nome del candidato"
                required
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="cognome" className="text-sm font-medium text-gray-700">Cognome *</Label>
              <Input
                id="cognome"
                value={formData.cognome}
                onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                placeholder="Cognome del candidato"
                required
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="telefono" className="text-sm font-medium text-gray-700">Telefono *</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                placeholder="+39 000 000 0000"
                required
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@candidato.it"
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="zona_interesse" className="text-sm font-medium text-gray-700">Zona di Interesse</Label>
              <Input
                id="zona_interesse"
                value={formData.zona_interesse}
                onChange={(e) => setFormData({...formData, zona_interesse: e.target.value})}
                placeholder="Milano, Roma, etc."
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="provenienza" className="text-sm font-medium text-gray-700">Provenienza</Label>
              <Select value={formData.provenienza} onValueChange={(value) => setFormData({...formData, provenienza: value})}>
                <SelectTrigger className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 shadow-xl rounded-2xl">
                  <SelectItem value="Telefonista" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Telefonista</SelectItem>
                  <SelectItem value="Facebook Ads" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Facebook Ads</SelectItem>
                  <SelectItem value="Instagram Ads" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Instagram Ads</SelectItem>
                  <SelectItem value="Altro" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label htmlFor="stato_pipeline" className="text-sm font-medium text-gray-700">Stato Pipeline</Label>
              <Select value={formData.stato_pipeline} onValueChange={(value) => setFormData({...formData, stato_pipeline: value})}>
                <SelectTrigger className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 shadow-xl rounded-2xl">
                  {Object.values(StatoPipeline).map(status => (
                    <SelectItem key={status} value={status} className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label htmlFor="anni_esperienza" className="text-sm font-medium text-gray-700">Anni Esperienza</Label>
              <Input
                id="anni_esperienza"
                type="number"
                min="0"
                max="50"
                value={formData.anni_esperienza}
                onChange={(e) => setFormData({...formData, anni_esperienza: parseInt(e.target.value) || 0, esperienza_flag: parseInt(e.target.value) > 0})}
                className="h-12 bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label htmlFor="note_telefonista" className="text-sm font-medium text-gray-700">Note Telefonista</Label>
            <Textarea
              id="note_telefonista"
              value={formData.note_telefonista}
              onChange={(e) => setFormData({...formData, note_telefonista: e.target.value})}
              placeholder="Note dal colloquio telefonico..."
              rows={3}
              className="bg-white/70 border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 resize-none"
            />
          </div>

          <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-2xl border border-blue-200">
            <Checkbox
              id="consenso_gdpr"
              checked={formData.consenso_gdpr}
              onCheckedChange={(checked) => setFormData({...formData, consenso_gdpr: checked})}
              className="rounded-lg"
            />
            <Label htmlFor="consenso_gdpr" className="text-sm text-gray-700 leading-relaxed">
              Accetto il trattamento dei dati personali secondo il GDPR *
            </Label>
          </div>

          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="flex-1 h-12 rounded-2xl border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Annulla
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Salvando...
                </div>
              ) : (
                "Crea Candidato"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Note Modal Component - Apple Style
const AggiungiNotaModal = ({ candidato, isOpen, onClose, onNotaAdded }) => {
  const [testo, setTesto] = useState("");
  const [esito, setEsito] = useState("");
  const [loading, setLoading] = useState(false);
  const [agenziaId, setAgenziaId] = useState("");
  const [agenzie, setAgenzie] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const loadAgenzie = async () => {
        try {
          const response = await axios.get(`${API}/api/v1/agenzie`);
          setAgenzie(response.data);
          if (response.data.length > 0) {
            setAgenziaId(response.data[0].id);
          }
        } catch (error) {
          console.error("Errore nel caricamento agenzie:", error);
          toast.error("Errore nel caricamento agenzie");
        }
      };
      loadAgenzie();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!testo.trim() || !esito || !agenziaId) {
      toast.error("Compila tutti i campi richiesti");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/candidati/${candidato.id}/note`, {
        testo: testo.trim(),
        esito,
        agenzia_id: agenziaId
      });

      toast.success("Nota aggiunta con successo");
      onNotaAdded();
      onClose();
      setTesto("");
      setEsito("");
    } catch (error) {
      console.error("Errore nell'aggiunta della nota:", error);
      toast.error("Errore nell'aggiunta della nota");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTesto("");
    setEsito("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl max-w-md w-full p-8 shadow-2xl border border-white/20 animate-in slide-in-from-bottom-4 duration-300">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Edit className="w-5 h-5 text-white" />
            </div>
            Aggiungi Nota
          </h3>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClose}
            className="rounded-full w-8 h-8 p-0 hover:bg-gray-100 transition-colors duration-200"
          >
            <X className="w-4 h-4 text-gray-500" />
          </Button>
        </div>
        
        <div className="bg-blue-50 rounded-2xl p-4 mb-6 border border-blue-100">
          <p className="text-sm text-gray-700">
            <span className="font-medium text-blue-700">Candidato:</span> 
            <span className="font-bold text-gray-900 ml-2">{candidato.nome} {candidato.cognome}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="agenzia" className="text-sm font-medium text-gray-700">Agenzia *</Label>
            <Select value={agenziaId} onValueChange={setAgenziaId} required>
              <SelectTrigger className="h-12 bg-white/70 border-gray-200 rounded-2xl text-gray-900 shadow-sm hover:shadow-md transition-all duration-200">
                <SelectValue placeholder="Seleziona agenzia" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200 shadow-xl rounded-2xl">
                {agenzie.map(agenzia => (
                  <SelectItem 
                    key={agenzia.id} 
                    value={agenzia.id} 
                    className="text-gray-900 hover:bg-blue-50 rounded-xl m-1"
                  >
                    {agenzia.nome_agenzia}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <Label htmlFor="esito" className="text-sm font-medium text-gray-700">Esito Colloquio *</Label>
            <Select value={esito} onValueChange={setEsito} required>
              <SelectTrigger className="h-12 bg-white/70 border-gray-200 rounded-2xl text-gray-900 shadow-sm hover:shadow-md transition-all duration-200">
                <SelectValue placeholder="Seleziona esito" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200 shadow-xl rounded-2xl">
                <SelectItem value="positivo" className="text-gray-900 hover:bg-green-50 rounded-xl m-1">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Positivo
                  </span>
                </SelectItem>
                <SelectItem value="negativo" className="text-gray-900 hover:bg-red-50 rounded-xl m-1">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    Negativo
                  </span>
                </SelectItem>
                <SelectItem value="da richiamare" className="text-gray-900 hover:bg-amber-50 rounded-xl m-1">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    Da richiamare
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <Label htmlFor="nota" className="text-sm font-medium text-gray-700">Nota *</Label>
            <Textarea
              id="nota"
              value={testo}
              onChange={(e) => setTesto(e.target.value)}
              placeholder="Inserisci i dettagli del colloquio..."
              required
              maxLength={1000}
              rows={4}
              className="bg-white/70 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-500 resize-none shadow-sm hover:shadow-md transition-all duration-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
            <p className="text-xs text-gray-500 flex justify-between items-center">
              <span>Descrivi l'esito del colloquio</span>
              <span className="font-medium">{testo.length}/1000</span>
            </p>
          </div>

          <div className="flex gap-3 pt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              className="flex-1 h-12 rounded-2xl border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Annulla
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Salvando...
                </div>
              ) : (
                "Salva Nota"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Candidate Details Component
const CandidatoDetails = ({ candidato, onClose, onUpdate }) => {
  const [note, setNote] = useState([]);
  const [loadingNote, setLoadingNote] = useState(true);

  useEffect(() => {
    fetchNote();
  }, [candidato]);

  const fetchNote = async () => {
    try {
      setLoadingNote(true);
      const response = await axios.get(`${API}/candidati/${candidato.id}/note`);
      setNote(response.data.note || []);
    } catch (error) {
      console.error("Errore nel caricamento note:", error);
      toast.error("Errore nel caricamento note");
    } finally {
      setLoadingNote(false);
    }
  };

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 max-w-5xl mx-auto border border-white/20 animate-in slide-in-from-bottom-4 duration-300">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center shadow-lg">
            <User className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
              {candidato.nome} {candidato.cognome}
            </h2>
            <p className="text-gray-600 mt-1">Profilo candidato</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={onClose}
          className="rounded-2xl border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 shadow-sm hover:shadow-md transition-all duration-200"
        >
          <X className="w-4 h-4 mr-2" />
          Chiudi
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900">Informazioni Personali</h3>
          <div className="bg-gray-50/80 backdrop-blur-sm rounded-3xl p-6 space-y-4 border border-gray-100 shadow-lg">
            <div className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-sm">
              <Phone className="w-5 h-5 text-blue-500" />
              <div>
                <span className="text-sm font-medium text-gray-500">Telefono</span>
                <p className="text-gray-900 font-medium">{candidato.telefono}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-sm">
              <Mail className="w-5 h-5 text-blue-500" />
              <div>
                <span className="text-sm font-medium text-gray-500">Email</span>
                <p className="text-gray-900 font-medium">{candidato.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-sm">
              <MapPin className="w-5 h-5 text-blue-500" />
              <div>
                <span className="text-sm font-medium text-gray-500">Zona di interesse</span>
                <p className="text-gray-900 font-medium">{candidato.zona_interesse}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-sm">
              <Target className="w-5 h-5 text-blue-500" />
              <div>
                <span className="text-sm font-medium text-gray-500">Esperienza</span>
                <p className="text-gray-900 font-medium">
                  {candidato.esperienza_flag ? `${candidato.anni_esperienza} anni` : "Nessuna esperienza"}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-sm">
              <CheckCircle className="w-5 h-5 text-blue-500" />
              <div className="flex items-center justify-between w-full">
                <div>
                  <span className="text-sm font-medium text-gray-500">Stato</span>
                  <div className="mt-1">
                    <Badge className={`${getStatusColor(candidato.stato_pipeline)} border-0 font-medium px-3 py-1 rounded-full`}>
                      {candidato.stato_pipeline}
                    </Badge>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-500">Punteggio</span>
                  <div className={`text-2xl font-bold ${getScoreColor(candidato.candidate_score)}`}>
                    {candidato.candidate_score}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">Note Colloqui</h3>
            <Badge className="bg-blue-100 text-blue-700 border-0 font-bold px-3 py-1 rounded-full">
              {note.length} {note.length === 1 ? 'nota' : 'note'}
            </Badge>
          </div>
          
          {loadingNote ? (
            <div className="bg-gray-50/80 rounded-3xl p-8 text-center border border-gray-100 shadow-lg">
              <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Caricamento note...</p>
            </div>
          ) : note.length > 0 ? (
            <div className="bg-gray-50/80 backdrop-blur-sm rounded-3xl p-6 space-y-4 max-h-96 overflow-y-auto border border-gray-100 shadow-lg">
              {note.map((nota, index) => (
                <div key={index} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {nota.esito === 'positivo' && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span className="text-sm font-medium text-green-700">Positivo</span>
                        </div>
                      )}
                      {nota.esito === 'negativo' && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <span className="text-sm font-medium text-red-700">Negativo</span>
                        </div>
                      )}
                      {nota.esito === 'da richiamare' && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                          <span className="text-sm font-medium text-amber-700">Da richiamare</span>
                        </div>
                      )}
                    </div>
                    <span className="text-xs font-medium text-gray-500">
                      {new Date(nota.data).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">{nota.testo}</p>
                  <div className="text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2">
                    <span className="font-medium">Agenzia:</span> {nota.agenzia_nome}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50/80 rounded-3xl p-8 text-center border border-gray-100 shadow-lg">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Edit className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">Nessuna nota presente</p>
              <p className="text-sm text-gray-500 mt-1">Le note dei colloqui appariranno qui</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main App Component
function MainApp() {
  const [currentView, setCurrentView] = useState('candidati');
  const [candidati, setCandidati] = useState([]);
  const [agenzie, setAgenzie] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("candidati");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTermAgenzie, setSearchTermAgenzie] = useState("");
  const [agenziaStatusFilter, setAgenziaStatusFilter] = useState("all");
  const [selectedCandidato, setSelectedCandidato] = useState(null);
  const [selectedAgenzia, setSelectedAgenzia] = useState(null);
  const [showCandidatoModal, setShowCandidatoModal] = useState(false);
  const [showAgenziaModal, setShowAgenziaModal] = useState(false);
  const [candidatoForm, setCandidatoForm] = useState({
    nome: "", 
    cognome: "", 
    telefono: "", 
    email: "", 
    eta: "", 
    citta: "",
    // Nuovi campi per match intelligente
    zona_preferenza_lavorativa: "",
    esperienza_immobiliare: false,
    tipo_impiego_preferito: "Full-time",
    mobilita_trasporto: "Auto"
  });
  const [agenziaForm, setAgenziaForm] = useState({
    nome_agenzia: "", zona: "", telefono_referente: "", email_referente: "", contatto_referente: ""
  });
  const [showAddNoteModal, setShowAddNoteModal] = useState(null);
  const [showNuovaAgenziaModal, setShowNuovaAgenziaModal] = useState(false);
  const [showNuovoCandidatoModal, setShowNuovoCandidatoModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showInviteCandidatoModal, setShowInviteCandidatoModal] = useState(false);
  const [showInviteAgenziaModal, setShowInviteAgenziaModal] = useState(false);

  // Fetch data
  const handleCandidatoSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/candidati`, candidatoForm);
      toast.success("Candidato registrato con successo!");
      setShowCandidatoModal(false);
      setCandidatoForm({ 
        nome: "", cognome: "", telefono: "", email: "", eta: "", citta: "",
        zona_preferenza_lavorativa: "", esperienza_immobiliare: false, 
        tipo_impiego_preferito: "Full-time", mobilita_trasporto: "Auto"
      });
      fetchData(); // Refresh data
    } catch (error) {
      toast.error("Errore nella registrazione candidato");
      console.error("Error:", error);
    }
  };

  const handleAgenziaSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/v1/agenzie`, agenziaForm);
      toast.success("Agenzia registrata con successo!");
      setShowAgenziaModal(false);
      setAgenziaForm({ nome_agenzia: "", zona: "", telefono_referente: "", email_referente: "", contatto_referente: "" });
      fetchData(); // Refresh data
    } catch (error) {
      toast.error("Errore nella registrazione agenzia");
      console.error("Error:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [candidatiRes, agenzieRes] = await Promise.all([
        axios.get(`${API}/api/v1/candidati`),
        axios.get(`${API}/api/v1/agenzie`)
      ]);
      
      setCandidati(candidatiRes.data);
      setAgenzie(agenzieRes.data);
    } catch (error) {
      console.error("Errore nel caricamento dati:", error);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentView !== 'login') {
      fetchData();
    }
  }, [currentView]);

  // Filter candidati
  const filteredCandidati = candidati.filter(candidato => {
    const matchesSearch = 
      candidato.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidato.cognome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidato.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidato.telefono.includes(searchTerm);
    
    const matchesStatus = !statusFilter || statusFilter === "all" || candidato.stato_pipeline === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Filter agenzie
  const filteredAgenzie = agenzie.filter(agenzia => {
    const matchesSearch = 
      agenzia.nome_agenzia.toLowerCase().includes(searchTermAgenzie.toLowerCase()) ||
      (agenzia.zona && agenzia.zona.toLowerCase().includes(searchTermAgenzie.toLowerCase())) ||
      (agenzia.citta && agenzia.citta.toLowerCase().includes(searchTermAgenzie.toLowerCase()));
    
    const matchesZone = agenziaStatusFilter === "all" || !agenziaStatusFilter || 
      (agenzia.zona && agenzia.zona.toLowerCase().includes(agenziaStatusFilter.toLowerCase()));
    
    return matchesSearch && matchesZone;
  });

  // Calculate stats
  const stats = Object.keys(StatoPipeline).reduce((acc, key) => {
    acc[StatoPipeline[key]] = candidati.filter(c => c.stato_pipeline === StatoPipeline[key]).length;
    return acc;
  }, {});

  // Render candidate card - Apple Style with proper shadows
  const renderCandidatoCard = (candidato) => (
    <Card key={candidato.id} className="bg-white/95 backdrop-blur-xl border-0 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 rounded-3xl shadow-lg overflow-hidden group">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl font-bold text-gray-900 mb-2" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
              {candidato.nome} {candidato.cognome}
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                  <span className={`text-sm font-bold text-white`}>
                    {Math.round(candidato.candidate_score)}
                  </span>
                </div>
                <span className={`text-sm font-medium ${getScoreColor(candidato.candidate_score)}`}>
                  Punteggio
                </span>
              </div>
              <Badge className={`${getStatusColor(candidato.stato_pipeline)} border-0 font-medium px-3 py-1 rounded-full`}>
                {candidato.stato_pipeline}
              </Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 space-y-4">
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center">
              <Phone className="w-4 h-4 text-blue-500" />
            </div>
            <span className="text-gray-700 font-medium">{candidato.telefono}</span>
          </div>
          
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center">
              <Mail className="w-4 h-4 text-blue-500" />
            </div>
            <span className="text-gray-700 font-medium truncate">{candidato.email}</span>
          </div>
          
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center">
              <MapPin className="w-4 h-4 text-blue-500" />
            </div>
            <span className="text-gray-700 font-medium">
              {candidato.zona_interesse || candidato.citta || 'Zona non specificata'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-medium px-3 py-1 rounded-full">
            {candidato.provenienza}
          </Badge>
          {candidato.eta && (
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 font-medium px-3 py-1 rounded-full">
              {candidato.eta} anni
            </Badge>
          )}
          {candidato.citta && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 font-medium px-3 py-1 rounded-full">
              {candidato.citta}
            </Badge>
          )}
          {candidato.esperienza_flag && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-medium px-3 py-1 rounded-full">
              {candidato.anni_esperienza}a exp
            </Badge>
          )}
        </div>

        {candidato.note_telefonista && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mt-3 shadow-sm">
            <p className="text-xs text-amber-800 leading-relaxed italic">
              "{candidato.note_telefonista}"
            </p>
          </div>
        )}

        <div className="flex gap-2 pt-4 border-t border-gray-100">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setSelectedCandidato(candidato)}
            className="flex-1 rounded-2xl border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
          >
            <User className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Dettagli</span>
            <span className="sm:hidden">Info</span>
          </Button>
          <Button 
            size="sm" 
            onClick={() => setShowAddNoteModal(candidato)}
            className="flex-1 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium"
          >
            <Edit className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Note</span>
            <span className="sm:hidden">📝</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Login Screen - Apple Style Redesigned
  if (currentView === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50/30 via-white to-blue-50/50 flex items-center justify-center px-4 py-8" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
        <div className="w-full max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
          
          {/* Main Container - Desktop: Horizontal Layout, Mobile: Vertical */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-16 min-h-[80vh]">
            
            {/* Logo Section - Left side on desktop, centered on mobile */}
            <div className="flex-shrink-0 text-center lg:text-left">
              <div className="mb-8 flex items-center justify-center lg:justify-start">
                <div className="w-20 h-20 mr-0 lg:mr-6 mb-4 lg:mb-0 bg-white rounded-3xl flex items-center justify-center" 
                     style={{
                       boxShadow: '0 10px 40px -10px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
                       background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
                     }}>
                  <img 
                    src={agentAdvancerSenzaSfondo} 
                    alt="InLeads Logo" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                
                {/* Text next to logo on desktop, below on mobile */}
                <div className="hidden lg:block">
                  <h1 className="text-4xl font-bold text-gray-900 mb-1" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
                    InLeads
                  </h1>
                  <p className="text-lg text-gray-600 font-medium">App Recruiting – Real Estate</p>
                  <p className="text-gray-500 text-sm">Accesso Riservato</p>
                </div>
              </div>
              
              {/* Text below logo on mobile */}
              <div className="lg:hidden">
                <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
                  InLeads
                </h1>
                <p className="text-lg text-gray-600 mb-2 font-medium">App Recruiting – Real Estate</p>
                <p className="text-gray-500 text-sm">Accesso Riservato</p>
              </div>
            </div>

            {/* Login Form Section - Right side on desktop, below on mobile */}
            <div className="w-full max-w-md flex-shrink-0">
              <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-white/40" 
                   style={{
                     boxShadow: '0 20px 60px -10px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.9)'
                   }}>
                
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Accedi al Sistema
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Inserisci le tue credenziali per continuare
                  </p>
                </div>
                
                <form className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-gray-700 text-sm font-medium block">
                      Username
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                        <Users className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        placeholder="Inserisci il tuo username"
                        className="w-full pl-12 pr-4 py-4 bg-gray-50/70 border border-gray-200/60 rounded-2xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-300 transition-all duration-200 shadow-sm hover:shadow-md"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-gray-700 text-sm font-medium block">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="password"
                        placeholder="Inserisci la tua password"
                        className="w-full pl-12 pr-12 py-4 bg-gray-50/70 border border-gray-200/60 rounded-2xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-300 transition-all duration-200 shadow-sm hover:shadow-md"
                      />
                      <button type="button" className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                        <Eye className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="button"
                    onClick={() => setCurrentView('dashboard')}
                    className="w-full h-14 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.01] mt-8"
                  >
                    Accedi alla App
                  </Button>
                </form>
              </div>
            </div>
          </div>
          
          {/* Bottom spacing for mobile */}
          <div className="h-8 lg:h-0"></div>
        </div>
      </div>
    );
  }

  // Dashboard App - Apple Style Premium with darker background
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-blue-100 pb-28 md:pb-8" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-7xl">
        {/* Header Ottimizzato Mobile-First */}
        <div className="mb-4 sm:mb-8">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-3 sm:p-6 shadow-lg sm:shadow-2xl border border-white/20">
            <div className="flex items-center justify-between gap-2 sm:gap-6">
              {/* Logo compatto su mobile */}
              <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white rounded-2xl sm:rounded-3xl shadow-lg flex items-center justify-center flex-shrink-0" 
                     style={{
                       boxShadow: '0 10px 40px -10px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
                       background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
                     }}>
                  <img 
                    src={agentAdvancerSenzaSfondo} 
                    alt="InLeads Logo" 
                    className="w-6 h-6 sm:w-9 sm:h-9 object-contain"
                  />
                </div>
                <div className="text-left min-w-0">
                  <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 truncate">
                    InLeads
                  </h1>
                  <p className="text-blue-600 font-medium text-xs sm:text-sm">Recruitment App</p>
                </div>
              </div>
              
              {/* Pulsanti Header Ottimizzati */}
              <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                {/* Nuovo Accesso - Compatto su mobile */}
                <Button
                  onClick={() => window.location.href = '/accesso'}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg sm:rounded-xl px-3 sm:px-6 py-2 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-xs sm:text-sm whitespace-nowrap"
                >
                  <span className="hidden sm:inline">🚀 Nuovo Accesso</span>
                  <span className="sm:hidden">+ Nuovo</span>
                </Button>
                
                {/* Desktop Menu - nascosto su mobile */}
                <div className="hidden lg:flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50 rounded-lg px-3 text-xs"
                    onClick={() => window.location.href = '/admin'}
                  >
                    Admin
                  </Button>
                  <Button
                    onClick={() => setCurrentView('login')}
                    variant="outline"
                    className="rounded-lg border-red-200 text-red-600 hover:bg-red-50 px-3 py-2 shadow-sm transition-all duration-200 text-xs"
                  >
                    <LogOut className="w-3 h-3 mr-1" />
                    Logout
                  </Button>
                </div>

                {/* Mobile Menu Button - Solo tablet e mobile */}
                <Button
                  variant="outline"
                  className="lg:hidden rounded-lg w-8 h-8 sm:w-10 sm:h-10 p-0 border-gray-300 hover:bg-gray-50"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <X className="w-3 h-3 sm:w-4 sm:h-4" /> : <Menu className="w-3 h-3 sm:w-4 sm:h-4" />}
                </Button>
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
              <div className="md:hidden mt-4 pt-4 border-t border-gray-200">
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-gray-700 hover:bg-gray-50 rounded-2xl"
                    onClick={() => {setActiveTab("candidati"); setMobileMenuOpen(false);}}
                  >
                    <Users className="w-4 h-4 mr-3" />
                    Candidati
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-gray-700 hover:bg-gray-50 rounded-2xl"
                    onClick={() => {setActiveTab("agenzie"); setMobileMenuOpen(false);}}
                  >
                    <Building2 className="w-4 h-4 mr-3" />
                    Agenzie
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-blue-600 hover:bg-blue-50 rounded-2xl"
                    onClick={() => window.location.href = '/login-candidato'}
                  >
                    <User className="w-4 h-4 mr-3" />
                    Login Candidato
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-blue-600 hover:bg-blue-50 rounded-2xl"
                    onClick={() => window.location.href = '/login-agenzia'}
                  >
                    <Building2 className="w-4 h-4 mr-3" />
                    Login Agenzia
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-blue-600 hover:bg-blue-50 rounded-2xl"
                    onClick={() => window.location.href = '/admin'}
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    Pannello Admin
                  </Button>
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-red-600 hover:bg-red-50 rounded-2xl"
                      onClick={() => {setCurrentView('login'); setMobileMenuOpen(false);}}
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Logout
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Candidate Detail View */}
        {selectedCandidato && (
          <div className="mb-8">
            <CandidatoDetails
              candidato={selectedCandidato}
              onClose={() => setSelectedCandidato(null)}
              onUpdate={fetchData}
            />
          </div>
        )}

        {/* Main Dashboard */}
        {!selectedCandidato && (
          <div className="space-y-8">
            {/* Tabs Apple Style */}
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/20">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-gray-100 rounded-2xl p-2 h-14">
                  <TabsTrigger 
                    value="candidati"
                    className="rounded-xl font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:text-blue-600 transition-all duration-200"
                  >
                    <Users className="w-5 h-5 mr-3" />
                    <span className="hidden sm:inline">Candidati</span>
                    <span className="sm:hidden">({candidati.length})</span>
                    <Badge className="ml-2 bg-blue-100 text-blue-700 border-0 hidden sm:inline-flex">
                      {candidati.length}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="agenzie"
                    className="rounded-xl font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:text-blue-600 transition-all duration-200"
                  >
                    <Building2 className="w-5 h-5 mr-3" />
                    <span className="hidden sm:inline">Agenzie</span>
                    <span className="sm:hidden">({agenzie.length})</span>
                    <Badge className="ml-2 bg-blue-100 text-blue-700 border-0 hidden sm:inline-flex">
                      {agenzie.length}
                    </Badge>
                  </TabsTrigger>
                </TabsList>

                {/* Candidati Tab */}
                <TabsContent value="candidati" className="space-y-8 mt-8">
                  {/* Search, Filters and Action Buttons */}
                  <div className="flex flex-col gap-4">
                    {/* Search and Filter Row */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <div className="relative">
                          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <Input
                            placeholder="Cerca candidati per nome, email, telefono..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-12 h-12 bg-gray-50/50 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-500 shadow-sm hover:shadow-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200"
                          />
                        </div>
                      </div>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full sm:w-56 h-12 bg-gray-50/50 border-gray-200 rounded-2xl text-gray-900 shadow-sm hover:shadow-md transition-all duration-200">
                          <Filter className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Filtra per status" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 shadow-xl rounded-2xl">
                          <SelectItem value="all" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Tutti gli stati</SelectItem>
                          {Object.values(StatoPipeline).map(status => (
                            <SelectItem key={status} value={status} className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">{status}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Action Buttons Row */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={() => setShowNuovoCandidatoModal(true)}
                        className="flex-1 sm:flex-none h-12 px-6 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Nuovo Candidato
                      </Button>
                      <Button
                        onClick={() => setShowInviteCandidatoModal(true)}
                        className="flex-1 sm:flex-none h-12 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Invia Modulo
                      </Button>
                    </div>
                  </div>

                  {/* Statistiche Candidati - Ottimizzate Mobile */}
                  <div className="space-y-4 sm:space-y-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 px-1">Statistiche Candidati</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 sm:gap-4">
                      <div className="bg-blue-100/90 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center border border-blue-200 shadow-md hover:shadow-lg transition-all duration-200">
                        <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-700 mb-1 sm:mb-2">{stats["Nuovo"] || 0}</div>
                        <div className="text-xs text-blue-600 font-medium leading-tight">Nuovo</div>
                      </div>
                      
                      <div className="bg-blue-200/90 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center border border-blue-300 shadow-md hover:shadow-lg transition-all duration-200">
                        <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-800 mb-1 sm:mb-2">{stats["Contattato"] || 0}</div>
                        <div className="text-xs text-blue-700 font-medium leading-tight">Contattato</div>
                      </div>
                      
                      <div className="bg-yellow-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-yellow-200 shadow-lg hover:shadow-xl transition-all duration-200">
                        <div className="text-3xl font-bold text-yellow-700 mb-2">{stats["Modulo inviato"] || 0}</div>
                        <div className="text-xs text-yellow-600 font-medium leading-tight">Modulo inviato</div>
                      </div>
                      
                      <div className="bg-purple-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-purple-200 shadow-lg hover:shadow-xl transition-all duration-200">
                        <div className="text-3xl font-bold text-purple-700 mb-2">{stats["Form compilato"] || 0}</div>
                        <div className="text-xs text-purple-600 font-medium leading-tight">Form compilato</div>
                      </div>
                      
                      <div className="bg-orange-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-orange-200 shadow-lg hover:shadow-xl transition-all duration-200">
                        <div className="text-3xl font-bold text-orange-700 mb-2">{stats["In attesa colloquio"] || 0}</div>
                        <div className="text-xs text-orange-600 font-medium leading-tight">In attesa colloquio</div>
                      </div>
                      
                      <div className="bg-cyan-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-cyan-200 shadow-lg hover:shadow-xl transition-all duration-200">
                        <div className="text-3xl font-bold text-cyan-700 mb-2">{stats["Colloquio fissato"] || 0}</div>
                        <div className="text-xs text-cyan-600 font-medium leading-tight">Colloquio fissato</div>
                      </div>
                      
                      <div className="bg-green-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-green-200 shadow-lg hover:shadow-xl transition-all duration-200">
                        <div className="text-3xl font-bold text-green-700 mb-2">{stats["Inserito"] || 0}</div>
                        <div className="text-xs text-green-600 font-medium leading-tight">Inserito</div>
                      </div>
                      
                      <div className="bg-red-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-red-200 shadow-lg hover:shadow-xl transition-all duration-200">
                        <div className="text-3xl font-bold text-red-700 mb-2">{stats["Scartato"] || 0}</div>
                        <div className="text-xs text-red-600 font-medium leading-tight">Scartato</div>
                      </div>
                    </div>
                  </div>

                  {/* Candidati List Apple Style */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 gap-y-8">
                    {loading ? (
                      <div className="col-span-full">
                        <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-12 text-center shadow-lg border border-gray-100">
                          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                          <p className="text-gray-600 font-medium">Caricamento candidati...</p>
                        </div>
                      </div>
                    ) : filteredCandidati.length > 0 ? (
                      filteredCandidati.map(renderCandidatoCard)
                    ) : (
                      <div className="col-span-full">
                        <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-12 text-center shadow-lg border border-gray-100">
                          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Users className="w-10 h-10 text-gray-400" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">Nessun candidato trovato</h3>
                          <p className="text-gray-600">Modifica i filtri di ricerca per vedere più risultati</p>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Agenzie Tab - Struttura identica a Candidati */}
                <TabsContent value="agenzie" className="space-y-6 sm:space-y-8 mt-6 sm:mt-8">
                  {/* Search, Filters and Action Buttons */}
                  <div className="flex flex-col gap-4">
                    {/* Search and Filter Row */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <div className="relative">
                          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <Input
                            placeholder="Cerca agenzie per nome, zona, città..."
                            value={searchTermAgenzie}
                            onChange={(e) => setSearchTermAgenzie(e.target.value)}
                            className="pl-12 h-12 bg-gray-50/50 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-500 shadow-sm hover:shadow-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200"
                          />
                        </div>
                      </div>
                      <Select value={agenziaStatusFilter} onValueChange={setAgenziaStatusFilter}>
                        <SelectTrigger className="w-full sm:w-56 h-12 bg-gray-50/50 border-gray-200 rounded-2xl text-gray-900 shadow-sm hover:shadow-md transition-all duration-200">
                          <Filter className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Filtra per zona" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 shadow-xl rounded-2xl">
                          <SelectItem value="all" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Tutte le zone</SelectItem>
                          <SelectItem value="Nord" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Nord</SelectItem>
                          <SelectItem value="Centro" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Centro</SelectItem>
                          <SelectItem value="Sud" className="text-gray-900 hover:bg-blue-50 rounded-xl m-1">Sud</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Action Buttons Row */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={() => setShowNuovaAgenziaModal(true)}
                        className="flex-1 sm:flex-none h-12 px-6 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Nuova Agenzia
                      </Button>
                      <Button
                        onClick={() => setShowInviteAgenziaModal(true)}
                        className="flex-1 sm:flex-none h-12 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Invia Modulo
                      </Button>
                    </div>
                  </div>

                  {/* Agenzie List */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 gap-y-8">
                    {loading ? (
                      <div className="col-span-full">
                        <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-12 text-center shadow-lg border border-gray-100">
                          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                          <p className="text-gray-600 font-medium">Caricamento agenzie...</p>
                        </div>
                      </div>
                    ) : filteredAgenzie.length > 0 ? (
                      filteredAgenzie.map(agenzia => (
                        <Card key={agenzia.id} className="bg-white/95 backdrop-blur-xl border-0 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 rounded-3xl shadow-lg overflow-hidden group">
                          <CardHeader className="pb-4">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                                <Building2 className="w-6 h-6 text-white flex-shrink-0" />
                              </div>
                              <div className="flex-1">
                                <CardTitle className="text-lg font-bold text-gray-900 leading-tight">{agenzia.nome_agenzia}</CardTitle>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pb-6">
                            <div className="space-y-5 overflow-visible">
                              <div className="space-y-3">
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl shadow-sm">
                                  <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  <div>
                                    <span className="text-xs font-medium text-gray-500">Zona</span>
                                    <p className="text-sm font-medium text-gray-900">{agenzia.zona}</p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl shadow-sm">
                                  <Target className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  <div>
                                    <span className="text-xs font-medium text-gray-500">Posti disponibili</span>
                                    <p className="text-sm font-bold text-blue-600">{agenzia.posti_disponibili}</p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl shadow-sm">
                                  <User className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  <div>
                                    <span className="text-xs font-medium text-gray-500">Profilo ricercato</span>
                                    <p className="text-sm font-medium text-gray-900">{agenzia.profilo_ricercato}</p>
                                  </div>
                                </div>
                                
                                {agenzia.contatto_referente && (
                                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl shadow-sm">
                                    <Phone className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                    <div>
                                      <span className="text-xs font-medium text-gray-500">Referente</span>
                                      <p className="text-sm font-medium text-gray-900">{agenzia.contatto_referente}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                                <Button 
                                  size="sm" 
                                  onClick={() => console.log('Modifica agenzia:', agenzia.id)}
                                  className="w-full sm:flex-1 h-9 rounded-2xl bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium text-xs px-3"
                                >
                                  <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                  <span className="hidden sm:inline">Modifica</span>
                                  <span className="sm:hidden">Modifica</span>
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => setShowInviteAgenziaModal(true)}
                                  className="w-full sm:flex-1 h-9 rounded-2xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium text-xs px-3"
                                >
                                  <Send className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                  <span className="hidden sm:inline">Invia Modulo</span>
                                  <span className="sm:hidden">Invita</span>
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => {
                                    // Apri dashboard agenzia specifica
                                    window.open(`/admin?agenzia=${agenzia.id}`, '_blank');
                                  }}
                                  className="w-full sm:flex-1 h-9 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium text-xs px-3"
                                >
                                  <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                  <span className="hidden sm:inline">Dashboard</span>
                                  <span className="sm:hidden">Dashboard</span>
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="col-span-full">
                        <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-12 text-center shadow-lg border border-gray-100">
                          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600 font-medium text-lg mb-2">Nessuna agenzia trovata</p>
                          <p className="text-gray-500">Prova a modificare i filtri di ricerca o aggiungi una nuova agenzia</p>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}

        {/* Modals */}
        <AggiungiNotaModal
          candidato={showAddNoteModal}
          isOpen={!!showAddNoteModal}
          onClose={() => setShowAddNoteModal(null)}
          onNotaAdded={fetchData}
        />

        <NuovaAgenziaModal
          isOpen={showNuovaAgenziaModal}
          onClose={() => setShowNuovaAgenziaModal(false)}
          onAgenziaAdded={fetchData}
        />

        <NuovoCandidatoModal
          isOpen={showNuovoCandidatoModal}
          onClose={() => setShowNuovoCandidatoModal(false)}
          onCandidatoAdded={fetchData}
        />

        {/* Modal Candidato Onboarding - v1.0 Intelligente */}
        {showCandidatoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Candidatura Intelligente</h3>
                  <p className="text-sm text-gray-600 mt-1">Compila tutti i campi per un match perfetto</p>
                </div>
                <Button variant="ghost" onClick={() => setShowCandidatoModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <form onSubmit={handleCandidatoSubmit} className="space-y-5">
                {/* Dati Personali */}
                <div className="bg-gray-50 rounded-2xl p-4 space-y-4">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <User className="w-4 h-4 mr-2 text-blue-600" />
                    Dati Personali
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Nome *</Label>
                      <Input
                        required
                        value={candidatoForm.nome}
                        onChange={(e) => setCandidatoForm({...candidatoForm, nome: e.target.value})}
                        className="rounded-xl h-12 text-base"
                        placeholder="Mario"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Cognome *</Label>
                      <Input
                        required
                        value={candidatoForm.cognome}
                        onChange={(e) => setCandidatoForm({...candidatoForm, cognome: e.target.value})}
                        className="rounded-xl h-12 text-base"
                        placeholder="Rossi"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Telefono *</Label>
                    <Input
                      required
                      type="tel"
                      value={candidatoForm.telefono}
                      onChange={(e) => setCandidatoForm({...candidatoForm, telefono: e.target.value})}
                      className="rounded-xl h-12 text-base"
                      placeholder="+39 333 123 4567"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Email</Label>
                    <Input
                      type="email"
                      value={candidatoForm.email}
                      onChange={(e) => setCandidatoForm({...candidatoForm, email: e.target.value})}
                      className="rounded-xl h-12 text-base"
                      placeholder="mario.rossi@email.com"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Età</Label>
                      <Input
                        type="number"
                        min="18"
                        max="80"
                        value={candidatoForm.eta}
                        onChange={(e) => setCandidatoForm({...candidatoForm, eta: e.target.value})}
                        className="rounded-xl h-12 text-base"
                        placeholder="30"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Città di residenza</Label>
                      <Input
                        value={candidatoForm.citta}
                        onChange={(e) => setCandidatoForm({...candidatoForm, citta: e.target.value})}
                        className="rounded-xl h-12 text-base"
                        placeholder="Milano"
                      />
                    </div>
                  </div>
                </div>

                {/* Preferenze Lavorative */}
                <div className="bg-blue-50 rounded-2xl p-4 space-y-4">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <Building2 className="w-4 h-4 mr-2 text-blue-600" />
                    Preferenze Lavorative
                  </h4>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Zona preferita per lavorare *</Label>
                    <Input
                      required
                      value={candidatoForm.zona_preferenza_lavorativa}
                      onChange={(e) => setCandidatoForm({...candidatoForm, zona_preferenza_lavorativa: e.target.value})}
                      className="rounded-xl h-12 text-base"
                      placeholder="Es. Milano centro, Roma nord, Napoli..."
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Esperienza nel settore immobiliare</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="esperienza"
                          checked={candidatoForm.esperienza_immobiliare === true}
                          onChange={() => setCandidatoForm({...candidatoForm, esperienza_immobiliare: true})}
                          className="text-blue-600"
                        />
                        <span className="text-gray-700">Sì, ho esperienza</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="esperienza"
                          checked={candidatoForm.esperienza_immobiliare === false}
                          onChange={() => setCandidatoForm({...candidatoForm, esperienza_immobiliare: false})}
                          className="text-blue-600"
                        />
                        <span className="text-gray-700">No, sono alle prime armi</span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Tipo di impiego preferito</Label>
                      <Select 
                        value={candidatoForm.tipo_impiego_preferito} 
                        onValueChange={(value) => setCandidatoForm({...candidatoForm, tipo_impiego_preferito: value})}
                      >
                        <SelectTrigger className="rounded-xl h-12 text-base">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Full-time">Full-time</SelectItem>
                          <SelectItem value="Part-time">Part-time</SelectItem>
                          <SelectItem value="Collaborazione">Collaborazione</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Mobilità / Trasporto</Label>
                      <Select 
                        value={candidatoForm.mobilita_trasporto} 
                        onValueChange={(value) => setCandidatoForm({...candidatoForm, mobilita_trasporto: value})}
                      >
                        <SelectTrigger className="rounded-xl h-12 text-base">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Auto">Auto propria</SelectItem>
                          <SelectItem value="Moto">Moto/Scooter</SelectItem>
                          <SelectItem value="Mezzi pubblici">Mezzi pubblici</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCandidatoModal(false)}
                    className="rounded-xl h-12 px-6 text-base order-2 sm:order-1"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    className="rounded-xl h-12 px-8 text-base font-semibold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl transition-all duration-200 order-1 sm:order-2"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Invia Candidatura
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Agenzia Onboarding */}
        {showAgenziaModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Registrazione Agenzia</h3>
                <Button variant="ghost" onClick={() => setShowAgenziaModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <form onSubmit={handleAgenziaSubmit} className="space-y-4">
                <div>
                  <Label>Nome Agenzia *</Label>
                  <Input
                    required
                    value={agenziaForm.nome_agenzia}
                    onChange={(e) => setAgenziaForm({...agenziaForm, nome_agenzia: e.target.value})}
                    className="rounded-2xl"
                    placeholder="Nome agenzia"
                  />
                </div>
                
                <div>
                  <Label>Zona *</Label>
                  <Input
                    required
                    value={agenziaForm.zona}
                    onChange={(e) => setAgenziaForm({...agenziaForm, zona: e.target.value})}
                    className="rounded-2xl"
                    placeholder="Zona di competenza"
                  />
                </div>
                
                <div>
                  <Label>Referente *</Label>
                  <Input
                    required
                    value={agenziaForm.contatto_referente}
                    onChange={(e) => setAgenziaForm({...agenziaForm, contatto_referente: e.target.value})}
                    className="rounded-2xl"
                    placeholder="Nome referente"
                  />
                </div>
                
                <div>
                  <Label>Telefono Referente *</Label>
                  <Input
                    required
                    type="tel"
                    value={agenziaForm.telefono_referente}
                    onChange={(e) => setAgenziaForm({...agenziaForm, telefono_referente: e.target.value})}
                    className="rounded-2xl"
                    placeholder="+39 123 456 7890"
                  />
                </div>
                
                <div>
                  <Label>Email Referente</Label>
                  <Input
                    type="email"
                    value={agenziaForm.email_referente}
                    onChange={(e) => setAgenziaForm({...agenziaForm, email_referente: e.target.value})}
                    className="rounded-2xl"
                    placeholder="email@agenzia.com"
                  />
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAgenziaModal(false)}
                    className="rounded-2xl"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    className="rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  >
                    Registra Agenzia
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Invito Candidato */}
        {showInviteCandidatoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">📩 Invita Candidato</h3>
                <button
                  onClick={() => setShowInviteCandidatoModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  ✕
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Copia questo messaggio e invialo al candidato per fargli completare la registrazione autonomamente:
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border">
                <textarea
                  readOnly
                  value={`🏢 Ciao! Ti invito a registrarti su InLeads, la piattaforma che connette candidati con le migliori agenzie immobiliari.

📝 REGISTRATI QUI: ${window.location.origin}/accesso

✅ Cosa otterrai:
• Match automatico con agenzie della tua zona
• Opportunità di colloqui personalizzati
• Supporto completo nel processo di selezione

È gratuito e ti bastano 2 minuti per completare il profilo!

Team InLeads 🚀`}
                  className="w-full h-48 text-sm bg-transparent border-none resize-none focus:outline-none"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`🏢 Ciao! Ti invito a registrarti su InLeads, la piattaforma che connette candidati con le migliori agenzie immobiliari.

📝 REGISTRATI QUI: ${window.location.origin}/accesso

✅ Cosa otterrai:
• Match automatico con agenzie della tua zona
• Opportunità di colloqui personalizzati
• Supporto completo nel processo di selezione

È gratuito e ti bastano 2 minuti per completare il profilo!

Team InLeads 🚀`);
                    toast.success('Testo copiato negli appunti!');
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  📋 Copia Testo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteCandidatoModal(false)}
                  className="flex-1"
                >
                  Chiudi
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Invito Agenzia */}
        {showInviteAgenziaModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">🏢 Invita Agenzia</h3>
                <button
                  onClick={() => setShowInviteAgenziaModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  ✕
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Copia questo messaggio e invialo all'agenzia per fargli completare la registrazione autonomamente:
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border">
                <textarea
                  readOnly
                  value={`👥 Ciao! Ti invito a registrarti su InLeads, la piattaforma che connette le migliori agenzie immobiliari con candidati qualificati.

🏢 REGISTRATI QUI: ${window.location.origin}/accesso

✅ Cosa otterrai:
• Accesso a candidati pre-selezionati
• Sistema di matching intelligente per la tua zona
• Dashboard completa per gestire colloqui e candidature
• Supporto dedicato per l'ottimizzazione del recruiting

È gratuito e professionale. Registrazione in 3 minuti!

Team InLeads 🚀`}
                  className="w-full h-48 text-sm bg-transparent border-none resize-none focus:outline-none"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`👥 Ciao! Ti invito a registrarti su InLeads, la piattaforma che connette le migliori agenzie immobiliari con candidati qualificati.

🏢 REGISTRATI QUI: ${window.location.origin}/accesso

✅ Cosa otterrai:
• Accesso a candidati pre-selezionati
• Sistema di matching intelligente per la tua zona
• Dashboard completa per gestire colloqui e candidature
• Supporto dedicato per l'ottimizzazione del recruiting

È gratuito e professionale. Registrazione in 3 minuti!

Team InLeads 🚀`);
                    toast.success('Testo copiato negli appunti!');
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  📋 Copia Testo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteAgenziaModal(false)}
                  className="flex-1"
                >
                  Chiudi
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

// Router App
function App() {
  return (
    <HashRouter>
      <div className="App">
        <Toaster position="top-right" richColors />
        
        <Routes>
          <Route path="/" element={<Navigate to="/accesso" replace />} />
          <Route path="/accesso" element={<LandingAccesso />} />
          <Route path="/demo" element={<DemoMain />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<MainApp />} />
          <Route path="/registrati" element={<SceltaTipoUtente />} />
          <Route path="/agenzia-onboarding" element={<AgenziaOnboarding />} />
          <Route path="/candidato-onboarding" element={<CandidatoOnboarding />} />
          <Route path="/onboarding-success" element={<OnboardingSuccess />} />
          <Route path="/dashboard-cliente" element={<DashboardCliente />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin-test" element={<AdminPanel />} />
          <Route path="/candidato/:candidatoId" element={<CandidatoDashboardWrapper />} />
          <Route path="/agenzia/:agenziaId" element={<AgenziaDashboardWrapper />} />
          <Route path="/dashboard-agenzia" element={<DashboardAgenzia />} />
          <Route path="/feed" element={<Feed />} />
          {/* Temporaneamente rimossa la regola catch-all per permettere routing SPA corretto */}
          <Route path="*" element={<div className="min-h-screen flex items-center justify-center"><p>Pagina non trovata. <a href="/accesso" className="text-blue-600 hover:underline">Torna alla home</a></p></div>} />
        </Routes>
      </div>
    </HashRouter>
  );
}

export default App;